import {ENDPOINT_DYNAMIC_DATA} from 'common/endpoints';
import {commonJwtHeader, removeEmptyIds} from 'common/utils';
import _ from 'lodash';

// get grants by organization id from dynamo
const getGrantsByOrganization = (token, orgId, programId) => {
  let endpoint = `${ENDPOINT_DYNAMIC_DATA}entities?entityType=grant&organizationId=${orgId}`;
  endpoint += removeEmptyIds(programId) ? `&programIds=${removeEmptyIds(programId)}` : '';
  return fetch(endpoint, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

// get applications by organization id from dynamo
const getApplicationsByOrganization = (token, orgId, programId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=application&organizationId=${orgId}${
      programId ? `&programIds=${removeEmptyIds(programId)}` : ''
    }`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get status of RFA by suborganization id, organization id, foid from dynamicdata
const getRfaStatusBySubOrganizationOrganizationFo = ({subOrgId, orgId, foId}) => {
  if (_.isUndefined(subOrgId) || _.isUndefined(orgId) || _.isUndefined(foId)) {
    return Promise.reject({message: 'missing arguments'});
  }
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?fundingOpportunityId=${foId}&entityType=rfa&organizationId=${orgId}&subOrganizationId=${subOrgId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get status of subApps by suborganization id, organization id, foid from dynamicdata
const getSubAppStatusBySubOrganizationOrganizationFo = ({subOrgId, orgId, foId}) => {
  if (_.isUndefined(subOrgId) || _.isUndefined(orgId) || _.isUndefined(foId)) {
    return Promise.reject({message: 'missing arguments'});
  }
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?fundingOpportunityId=${foId}&entityType=subApplication&organizationId=${orgId}&subOrganizationId=${subOrgId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get status of RFA by organization id, foid from dynamicdata
const getRfaStatusByOrganizationFo = (token, orgId, foId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?fundingOpportunityId=${foId}&entityType=rfa&organizationId=${orgId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

//new endpoint for getting subapplications
const getSubapplicationsByToken = (token) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}user-subapplications`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};
// get subapplications by organization id from dynamo
const getSubapplicationsByOrganization = (token, orgId, programId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=subApplication&organizationId=${orgId}${
      programId ? `&programIds=${removeEmptyIds(programId)}` : ''
    }`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// get subapplications by organization id and categoryId from dynamo
const getSubapplicationsByOrgAndCategoryId = (token, orgId, categoryId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=subApplication&organizationId=${orgId}&categoryId=${categoryId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

// required because RestApiCallListener only accepts functions using destructured parameters
const callGetEntityById = ({token, entityId}) => {
  return getEntityById(token, entityId);
};

// get entity by id
const getEntityById = (token, entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/${entityId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const createApplicationEntity = (token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const updateEntity = (token, entity) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entity),
  });
};

const patchEntity = (token, entity) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/${entity.entityId}`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(entity),
  });
};

const modifySubApp = (token, entity, entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}subapplications/${entityId}/payload`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(entity),
  });
};
const deleteEntity = (token, entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/${entityId}`, {
    method: 'DELETE',
    headers: commonJwtHeader(),
  });
};

const lock = (token, grantId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities/attachments/${grantId}?deletable=false`,
    {
      method: 'PUT',
      headers: commonJwtHeader(),
    }
  );
};

const unlock = (token, grantId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/attachments/${grantId}?deletable=true`, {
    method: 'PUT',
    headers: commonJwtHeader(),
  });
};

const lockEntities = (token, entitiesData) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/attachments?deletable=false`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entitiesData),
  });
};

const unlockEntities = (token, entitiesData) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/attachments?deletable=true`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(entitiesData),
  });
};

const updateBatchData = (token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const callGetAllByGrantIdAndEntityType = ({token, grantId, entityType}) => {
  return getAllByGrantIdAndEntityType(token, grantId, entityType);
};

const callGetAllBySubGrantsAndEntityType = ({token, subGrants, entityType}) => {
  return Promise.all(
    subGrants.map((subGrant) =>
      getAllByGrantIdAndEntityType(token, subGrant.grantId, entityType)
    )
  );
};

const getAllByGrantIdAndEntityType = (token, grantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=${entityType}&grantId=${grantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const getByGrantIdAndEntityType = (token, grantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities/first-entity?entityType=${entityType}&grantId=${grantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const getBySubGrantIdAndEntityType = (token, subGrantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities/first-entity?entityType=${entityType}&subGrantId=${subGrantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const callGetBySubGrantIdAndEntityType = ({token, subGrantId, entityType}) => {
  return getBySubGrantIdAndEntityType(token, subGrantId, entityType);
};

const createEntity = (token, grantId, entityType) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}grants/${grantId}/entities/${entityType}`, {
    method: 'POST',
    headers: commonJwtHeader(),
  });
};

const createEntityFromSubGrant = (token, subGrantId, entityType) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}subgrants/${subGrantId}/entities/${entityType}`, {
    method: 'POST',
    headers: commonJwtHeader(),
  });
};

const createGrant = (token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/grants`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const updateApplicationLinkEntities = (jwt_token, payload) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}application-links`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const deleteApplicationLinkEntity = (jwt_token, applicationLinkEntityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}application-links/${applicationLinkEntityId}`, {
    method: 'DELETE',
    headers: commonJwtHeader(),
  });
};

const getEntitiesBySubGrantIdAndEntityType = (subGrantId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=${entityType}&subGrantId=${subGrantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const deleteMultipleApplicationLinks = (
  jwt_token,
  applicationEntityId,
  applicationLinkEntities
) => {
  const payload = {
    ids: applicationLinkEntities,
  };
  return fetch(`${ENDPOINT_DYNAMIC_DATA}application-links`, {
    method: 'DELETE',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const getSubAmendmentRecommendationsForGrant = (grantId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=subAmendmentRecommendation&grantId=${grantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const getSubEntityByGrantIdTypeAndSubGrantId = ({grantId, entityType, subGrantId}) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities/first-entity?entityType=${entityType}&grantId=${grantId}&subGrantId=${subGrantId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const getEntityByOrgIdAndFOId = (organizationId, fundingOpportunityId, entityType) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=${entityType}&fundingOpportunityId=${fundingOpportunityId}&organizationId=${organizationId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const callGetEntityByOrgIdAndFOId = ({
  organizationId,
  fundingOpportunityId,
  entityType,
}) => {
  return getEntityByOrgIdAndFOId(organizationId, fundingOpportunityId, entityType);
};

const getEntityByEntityTypeAndFOId = (entityType, fundingOpportunityId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities?entityType=${entityType}&fundingOpportunityId=${fundingOpportunityId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const callGrantIdByFOIdAndOrgId = ({organizationId, fundingOpportunityId}) => {
  return grantIdByFOIdAndOrgId(organizationId, fundingOpportunityId);
};

const grantIdByFOIdAndOrgId = (organizationId, fundingOpportunityId) => {
  return fetch(
    `${ENDPOINT_DYNAMIC_DATA}entities/grant-id?fundingOpportunityId=${fundingOpportunityId}&organizationId=${organizationId}`,
    {
      method: 'GET',
      headers: commonJwtHeader(),
    }
  );
};

const putRiskSectionsToGrant = ({grantId, riskControls}) => {
  const payload = {
    payload: {
      riskControls,
    },
  };

  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/${grantId}`, {
    method: 'PUT',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const saveForLater = ({entityId, taskId, entityName, dataType}) => {
  const payload = {
    entityId,
    taskId,
    entityName,
    dataType,
  };

  return fetch(`${ENDPOINT_DYNAMIC_DATA}saved-for-later`, {
    method: 'POST',
    headers: commonJwtHeader(),
    body: JSON.stringify(payload),
  });
};

const getSavedForUserByEntityId = (entityId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saved-for-later/entities/${entityId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSavedForUserByTaskId = (taskId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saved-for-later/tasks/${taskId}`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const getSavedForUser = () => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saved-for-later`, {
    method: 'GET',
    headers: commonJwtHeader(),
  });
};

const unsaveForLater = (saveForLaterId) => {
  return fetch(`${ENDPOINT_DYNAMIC_DATA}saved-for-later/${saveForLaterId}`, {
    method: 'DELETE',
    headers: commonJwtHeader(),
  });
};

const patchEntitySubmittedToFEMA = ({entity}) => {
  const updatedEntity = {
    ...entity,
    status: 'submittedToFEMA',
  };
  return fetch(`${ENDPOINT_DYNAMIC_DATA}entities/${entity.entityId}`, {
    method: 'PATCH',
    headers: commonJwtHeader(),
    body: JSON.stringify(updatedEntity),
  });
};

export default {
  getGrantsByOrganization,
  getApplicationsByOrganization,
  getRfaStatusBySubOrganizationOrganizationFo,
  getSubAppStatusBySubOrganizationOrganizationFo,
  getRfaStatusByOrganizationFo,
  getSubapplicationsByToken,
  getSubapplicationsByOrganization,
  getSubapplicationsByOrgAndCategoryId,
  getEntitiesBySubGrantIdAndEntityType,
  updateEntity,
  patchEntity,
  patchEntitySubmittedToFEMA,
  modifySubApp,
  deleteEntity,
  updateBatchData,
  getEntityById,
  callGetAllByGrantIdAndEntityType,
  callGetAllBySubGrantsAndEntityType,
  getAllByGrantIdAndEntityType,
  getByGrantIdAndEntityType,
  getBySubGrantIdAndEntityType,
  callGetBySubGrantIdAndEntityType,
  createEntity,
  createEntityFromSubGrant,
  updateApplicationLinkEntities,
  createGrant,
  createApplicationEntity,
  deleteApplicationLinkEntity,
  deleteMultipleApplicationLinks,
  lock,
  unlock,
  lockEntities,
  unlockEntities,
  getSubAmendmentRecommendationsForGrant,
  getSubEntityByGrantIdTypeAndSubGrantId,
  putRiskSectionsToGrant,
  callGetEntityById,
  getEntityByOrgIdAndFOId,
  callGrantIdByFOIdAndOrgId,
  grantIdByFOIdAndOrgId,
  getEntityByEntityTypeAndFOId,
  callGetEntityByOrgIdAndFOId,
  saveForLater,
  getSavedForUserByEntityId,
  getSavedForUserByTaskId,
  getSavedForUser,
  unsaveForLater,
};
